import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import { DisposablesBasket } from '@/engine/Utilities/DisposablesBasket';
import { TYPES } from '@/engine/ioc/types';
import { useInjection } from 'inversify-react';
import React, { useEffect } from 'react';
import type * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import { BooleanTuner } from '@/engine/PlanningAlgorithm/Tuners/BooleanTuner';
import { CORE_TYPES } from '@/engine/app/modules/core';
import type { ICommand, ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import type { ITuneCommandFactory } from '@/engine/PlanningAlgorithm/Tuners/commands/interfaces';

export interface IBooleanTuner {
  doTune: (value: boolean) => void;
}

export const useBooleanTuner = (
  descriptor: string
): {
  value: boolean;
  tuner: IBooleanTuner;
} => {
  const planningAlgorithm = useInjection<PlanningAlgorithm>(TYPES.PlanningAlgorithm);
  const tuneCommandsFactory = useInjection<ITuneCommandFactory<ICommand, BooleanTuner, boolean>>(
    TYPES.BooleanTuneCommandsFactory
  );
  const commandProcessor = useInjection<ICommandProcessor>(CORE_TYPES.CommandProcessor);
  const [value, setValue] = React.useState(false);
  const [tuner, setTuner] = React.useState<IBooleanTuner>({
    doTune: (): void => {
      return;
    }
  });

  useEffect(() => {
    const algoTuner = new BooleanTuner(planningAlgorithm);

    const valueSetter = (val: boolean): void => {
      const command: ICommand = tuneCommandsFactory.createCommand(algoTuner, descriptor, val);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
      commandProcessor.execute(command);
    };

    const onValueChanged = (tuneValue?: DT.BooleanT): void => {
      if (!tuneValue) {
        throw new Error('tuneValue expected to be valid here');
      }
      setValue(tuneValue.value);
    };

    const subscriptions: DisposablesBasket = new DisposablesBasket();
    subscriptions.add(planningAlgorithm.addOnChangedCallbackFor<DT.BooleanT>(descriptor, onValueChanged));

    setValue(algoTuner.getValue(descriptor));
    setTuner({ doTune: valueSetter });

    return (): void => subscriptions.dispose();
  }, [planningAlgorithm, descriptor, tuneCommandsFactory, commandProcessor]);

  return { value: value, tuner: tuner };
};
